<script context="module">
    import { get } from "svelte/store";
    import {
        user,
        createUser,
        showApiErrorModal,
        isRetriedFetch,
        firstFetchErrorMsg,
    } from "../stores";
    import { HTTP_STATUS, X_Device_ID } from "../Utils/utilConstants.svelte";
    import {
        globalAbortController,
        refreshToken,
        showError,
    } from "../Utils/utilFunctions.svelte";
    import {
        serverUrl,
        checkTokenExpiration,
        signOut,
    } from "../Utils/utilFunctions.svelte";

    const noAuthUrlArr = ["LOGIN", "USER", "FORGOTPASSWORD"];

    const rawSourceResponse = ["MfaQrCode", "AccountSchedule"];

    const containsKeyword = (url, arr) =>
        arr.some((keyword) =>
            url.toUpperCase().includes(keyword.toUpperCase()),
        );

    const doFetch = async (url, data, isManuallyHandleError = false) => {
        const userData = get(user);
        const userToken = userData == null ? "" : userData.token;

        const createUserData = get(createUser);
        const createUserToken =
            createUserData == null ? "" : createUserData.token;

        if (
            userToken ||
            createUserToken ||
            noAuthUrlArr.includes(url.split("?")[0].toUpperCase()) ||
            url.toLowerCase().includes("user/verifyemail")
        ) {
            const tokenRefreshResult =
                noAuthUrlArr.includes(url.split("?")[0].toUpperCase()) ||
                url.toLowerCase().includes("user/verifyemail")
                    ? "valid"
                    : await checkTokenExpiration();

            if (
                tokenRefreshResult != "valid" &&
                tokenRefreshResult != "refreshed"
            ) {
                signOut({ reason: "Invalid token" }, true);
                return;
            }

            try {
                var requestObject = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Credentials": "true",
                        "Access-Control-Allow-Origin": "*",
                        "Access-Control-Allow-Methods": "POST",
                        Authorization: `Bearer ${
                            userToken ? userToken : createUserToken
                        }`,
                    },
                    mode: "cors",
                    cache: "no-cache",
                    signal: globalAbortController.signal,
                };

                requestObject.headers["X-Device-Id"] = X_Device_ID;

                if (data) {
                    if (typeof data === "object") data.source = `Dashboard${data.source ? ` (${data.source})` : ''}`;
                    requestObject.body = JSON.stringify(data);
                }
                const response = await fetch(
                    `${serverUrl}/${url}`,
                    requestObject,
                );

                if (response.ok) {
                    if (response.status === HTTP_STATUS.NO_CONTENT) return null;

                    if (containsKeyword(url, rawSourceResponse))
                        return await response.text();

                    let res = await response.json();

                    if (res.error && !isManuallyHandleError) {
                        console.log(res.error);
                        showError(
                            "please refresh the page and try again and contact support if the issue persists.",
                        );
                    }

                    return res;
                } else if (
                    response.status == HTTP_STATUS.SERVER_TIMEOUT ||
                    response.status == HTTP_STATUS.BAD_GATE_WAY ||
                    response.status == HTTP_STATUS.SERVICE_UNAVAILABLE ||
                    response.status == HTTP_STATUS.GATEWAY_TIMEOUT
                ) {
                    if (get(isRetriedFetch)) {
                        isRetriedFetch.set(false);
                        firstFetchErrorMsg.set("");
                        showApiErrorModal.set(true);
                        return;
                    }

                    isRetriedFetch.set(true);
                    const resText = await response.text();
                    firstFetchErrorMsg.set(resText);

                    setTimeout(async () => {
                        return await doFetch(url, data);
                    }, 1000);
                } else if (
                    response.status === HTTP_STATUS.UNAUTHORIZED ||
                    response.status === HTTP_STATUS.FORBIDDEN
                ) {
                    const tokenStatus = await checkTokenExpiration();

                    if (tokenStatus === "valid") {
                        // Refreshed from in another thread?
                        const currentUserData = get(user);
                        if (
                            currentUserData &&
                            currentUserData.token &&
                            currentUserData.token !== userToken
                        ) {
                            return await doFetch(url, data);
                        }

                        if (get(isRetriedFetch)) {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            isRetriedFetch.set(false);
                            return;
                        }

                        const localSavedUserData = JSON.parse(
                            localStorage.getItem("TciSavedUser"),
                        );

                        if (localSavedUserData) {
                            user.set({
                                ...localSavedUserData,
                            });
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000),
                        );

                        isRetriedFetch.set(true);

                        const refreshStatus = await refreshToken();

                        if (refreshStatus) {
                            return await doFetch(url, data);
                        } else {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            return;
                        }
                    } else if (tokenStatus === "refreshed") {
                        return await doFetch(url, data);
                    } else {
                        return;
                    }
                } else {
                    const errText = await response.text();
                    throw new Error(errText);
                }
            } catch (error) {
                throw error;
            }
        } else signOut({ reason: "Invalid token" }, true);
    };

    const logIn = async (data) => {
        const url = `login`;

        return await doFetch(url, data, true);
    };

    const signUp = async (data) => {
        const url = `User`;

        return await doFetch(url, data);
    };

    const sendVerificationEmail = async () => {
        const url = `User/sendVerifyEmailNotification`;

        return await doFetch(url);
    };

    const validateEmail = async (data) => {
        const url = `User/VerifyEmail`;

        return await doFetch(url, data);
    };

    const sendVerificationSms = async () => {
        const url = `User/sendVerifyPhoneNotification`;

        return await doFetch(url);
    };

    const validatePhone = async (data) => {
        const url = `User/VerifyPhone`;

        return await doFetch(url, data);
    };

    const getMfaQrCode = async (userId) => {
        const data = { userId, type: 4 };

        const url = `User/MfaQrCode`;

        return await doFetch(url, data);
    };

    const sendAccountSignature = async (data) => {
        const url = `image`;

        return await doFetch(url, data);
    };

    const addBillingRule = async (data) => {
        const url = `BillingRule`;

        return await doFetch(url, data);
    };

    const forgetPwd = async (data) => {
        const url = `ForgotPassword`;

        return await doFetch(url, data);
    };

    const createAdmin = async (data) => {
        const url = `Admin`;

        return await doFetch(url, data);
    };

    const createAccount = async (data) => {
        const url = `Account`;

        return await doFetch(url, data);
    };

    const createAccountAvatar = async (obj) => {
        const url = `image`;
        const data = { ...obj, type: 2 };

        return await doFetch(url, data);
    };

    const createAccountAvatarForPatient = async (obj) => {
        const url = `image`;
        const data = { ...obj, type: 3 };

        return await doFetch(url, data);
    };

    const createGroup = async (data) => {
        const url = `Group`;

        return await doFetch(url, data);
    };

    const createExternalGroupInvoice = async (data) => {
        const url = `ExternalGroupInvoice/createAllExternalGroupsInvoices`;

        return await doFetch(url, data);
    };

    const createAccountInvoice = async (data) => {
        const url = `AccountInvoice`;

        return await doFetch(url, data);
    };

    const sendInvoiceToDoctor = async (data) => {
        const url = `AccountInvoice/SendInvoice`;

        return await doFetch(url, data);
    };

    const createPatient = async (data) => {
        const url = `Patient`;

        return await doFetch(url, data);
    };

    const createPatientContact = async (data) => {
        const url = `PatientContact`;

        return await doFetch(url, data);
    };

    const createSurgeon = async (data) => {
        const url = `AccountDoctor`;

        return await doFetch(url, data);
    };

    const createPatientEncounter = async (data) => {
        const url = `PatientEncounter`;

        return await doFetch(url, data);
    };

    const sendNotifyPatient = async (id, sendNow = false) => {
        const url = `PatientEncounter/SendUpdateYourDetails/${id}?sendNow=${sendNow}`;

        return await doFetch(url);
    };

    const postPaymentEFTCSV = async (data) => {
        const url = `Payment/eftcsv`;

        return await doFetch(url, data);
    };

    const checkOPV = async (data, isUsingPatient = false) => {
        const url = isUsingPatient
            ? `Patient/OpvCheck`
            : `PatientEncounter/OpvCheck`;

        return await doFetch(url, data);
    };

    const matchPatient = async (data, doctorId) => {
        const url = `Patient/Match/${doctorId}`;

        return await doFetch(url, data, true);
    };

    const matchPatientEncounters = async (doctorId, data) => {
        const url = `PatientEncounter/Match${doctorId ? `?doctorId=${doctorId}` : ''}`;

        return await doFetch(url, data, true);
    };

    const checkEligibility = async (data) => {
        const url = `PatientEncounter/EligibilityCheck`;

        return await doFetch(url, data);
    };

    const claimPatientEncounter = async (data, overrideClaim = false) => {
        const url = `PatientEncounter/Claim${overrideClaim ? "?forceClaim=1" : ""}`;

        return await doFetch(url, data, true);
    };

    const claimEncounterStatus = async (data) => {
        const url = `PatientEncounter/ClaimStatus`;

        return await doFetch(url, data);
    };

    const claimEncounterEraStatus = async (data) => {
        const url = `PatientEncounter/ClaimEraStatus`;

        return await doFetch(url, data);
    };

    const addInsuranceFundToAccount = async (data) => {
        const url = `AccountInsuranceFundActive`;

        return await doFetch(url, data);
    };

    const createPayment = async (data) => {
        const url = `Payment`;

        return await doFetch(url, data);
    };

    const resentEncounterNotification = async (data) => {
        const url = `PatientEncounter/ReSendEncounterNotification`;

        return await doFetch(url, data);
    };

    const cancelEncounterCommunication = async (data) => {
        const url = `PatientEncounter/CancelEncounterNotification`;

        return await doFetch(url, data);
    };

    const sendEncounterCommunicationNow = async (data) => {
        const url = `PatientEncounter/SendEncounterNotificationNow`;

        return await doFetch(url, data);
    };

    const sendAccountOnboardingForm = async (data) => {
        const url = `Account/SendOnboardingForms/${data.accountId}`;

        return await doFetch(url, data);
    };

    const uploadAccountSchedulePDF = async (data) => {
        const url = `AccountSchedule/${data.accountId}`;

        return await doFetch(url, data, true);
    };

    const validateMfa = async (data) => {
        data.type = 4;

        const url = `MFa/Validate`;

        return await doFetch(url, data);
    };

    const regeneratePdfFile = async (data) => {
        const url = `File`;

        return await doFetch(url, data);
    };

    const uploadPatientDocument = async (data) => {
        const documentData = {
            ...data,
            type: Number(9),
        };

        const url = `File`;

        return await doFetch(url, documentData);
    };

    const uploadFile = async (file) => {
        const url = `File`;

        return await doFetch(url, file);
    };

    const createEstimateFromBillingItem = async (id) => {
        const url = `Estimate/CreateEstimateFromBillingItem/${id}`;

        return await doFetch(url);
    };

    const sendEstimateToPatient = async (data) => {
        const url = `Estimate/SendEstimate`;

        return await doFetch(url, data);
    };

    const createInvoiceFromBillingItem = async (id, isNoGapProgram) => {
        const url = `Invoice/CreateInvoiceFromBillingItem/${id}?autoSendNoGap=${isNoGapProgram ? "1" : "0"}`;

        return await doFetch(url);
    };

    const sendInvoiceToPatient = async (data) => {
        const url = `Invoice/SendInvoice`;

        return await doFetch(url, data);
    };

    const sendTopUpInvoice = async (encounterId) => {
        const url = `Invoice/SendTopUpInvoice/${encounterId}`;

        return await doFetch(url);
    };
    const sendInvoiceToSupport = async (invoice) => {
        const url = `Invoice/SendInvoiceToSupport`;

        return await doFetch(url, invoice);
    };

    const sendReceiptToPatient = async (data) => {
        const url = `Receipt/SendReceipt`;

        return await doFetch(url, data);
    };

    const createAnnouncement = async (data) => {
        const url = `Announcement`;

        return await doFetch(url, data);
    };

    const createOrderProduct = async (data) => {
        const url = `OrderProducts`;

        return await doFetch(url, data);
    };

    const createAgreementDocument = async (data) => {
        const url = `AgreementDocuments`;

        return await doFetch(url, data);
    };

    const createWorkcoverAuthority = async (data) => {
        const url = `WorkCoverAuthorities`;

        return await doFetch(url, data);
    };

    const createTrafficAccidentAuthority = async (data) => {
        const url = `TrafficAccidentAuthorities`;

        return await doFetch(url, data);
    };

    const createManager = async (data) => {
        const url = `Manager`;

        return await doFetch(url, data);
    };

    const createAccountLeave = async (data) => {
        const url = `AccountLeave`;

        return await doFetch(url, data);
    };

    const createAccountCalendarEvent = async (data) => {
        const url = `AccountCalendarEvent`;

        return doFetch(url, data);
    };

    const createGlobalEvent = async (data) => {
        const url = `GlobalCalendarEvent`;

        return doFetch(url, data);
    };

    const createNotification = async (type = "sms", data) => {
        const url =
            type == "sms"
                ? `PatientEncounter/SendSmsNotification`
                : type == "email"
                  ? `PatientEncounter/SendEmailNotification`
                  : "";

        if (url) return await doFetch(url, data);
    };

    const createAccountNotification = async (data) => {
        const url = `Account/SendEmailNotification/${data.doctorId}`;

        if (url) return await doFetch(url, data);
    };

    const calculateBillingRuleTotalFee = async (data) => {
        const url = `patientEncounterSummary/calculateTotals`;

        return await doFetch(url, data);
    };

    const getInvoiceReason = async (data) => {
        const url = `patientEncounterSummary/getreason`;

        return await doFetch(url, data);
    }

    const updateBillingRuleTotalFee = async (data) => {
        const url = `patientEncounterSummary/updateTotals`;

        return await doFetch(url, data);
    };

    const createInvoiceForHealthFund = async (id) => {
        const url = `invoice/CreateInvoiceForHealthFund/${id}`;

        return await doFetch(url);
    };

    const sendInvoiceToClaim = async (data) => {
        const url = `invoice/sendInvoice`;

        return await doFetch(url, data);
    };

    const createAccountProvider = async (data) => {
        const url = `AccountProvider`;

        return await doFetch(url, data);
    };

    const createSupportStaff = async (data) => {
        const url = `SupportStaff`;

        return await doFetch(url, data);
    };

    const createEmailTemplate = async (data) => {
        const url = `EmailTemplate`;

        return await doFetch(url, data);
    };

    const createSmsTemplate = async (data) => {
        const url = `SmsTemplate`;

        return await doFetch(url, data);
    };

    const createPdfTemplate = async (data) => {
        const url = `PdfTemplate`;

        return await doFetch(url, data);
    };

    const cancelNotifyPatient = async (id) => {
        const url = `PatientEncounter/CancelUpdateYourDetails/${id}`;

        return await doFetch(url);
    };

    const createNote = async (data) => {
        const url = "notes";

        return await doFetch(url, data);
    };

    const createPreOpInstruction = async (data) => {
        const url = `PreOpInstructions`;

        return await doFetch(url, data);
    };

    const createPreOpQuestionnaire = async (data) => {
        const url = `PreOpQuestionnaire`;

        return await doFetch(url, data);
    };

    const sendPreOpQuestionnaire = async (encounterId) => {
        const url = `PatientEncounter/SendPreOpQuestionnaireNotification/${encounterId}`;

        return await doFetch(url);
    };

    const createAccountPresets = async (data) => {
        const url = `AccountPresets`;

        return await doFetch(url, data);
    };

    const createOcrSticker = async (data) => {
        const url = `OcrSticker`;

        return await doFetch(url, data);
    };

    const createThirdPartyInsurer = async (data) => {
        const url = `AccountThirdPartyInsurers`;

        return await doFetch(url, data);
    };

    const createCalendarEventCategory = async (data) => {
        const url = `CalendarEventsCategories`;

        return await doFetch(url, data);
    };

    const createDefaultFilter = async (data) => {
        const url = `UsersFilters`;

        return await doFetch(url, data);
    };

    const createSurgeryRoom = async (data) => {
        const url = `SurgeryRooms`;

        return await doFetch(url, data);
    };

    const createFreeTextItem = async (data) => {
        const url = `PatientEncounterCode`;

        return await doFetch(url, data);
    };

    const paymentRefundCollectPatientBankDetails = async (data) => {
        const url = `Payment/RefuncvollectPatientBankDetails`;

        return await doFetch(url, data);
    };

    const createAccountInsuranceDefaultCover = async (data) => {
        const url = `AccountInsuranceDefaultCover`;

        return await doFetch(url, data);
    };

    const createBupaPracticeId = async (data) => {
        const url = `BupaPracticeId`;

        return await doFetch(url, data);
    };

    const createAccountThirdPartyInsurer = async (data) => {
        const url = `AccountThirdPartyInsurers`;

        return await doFetch(url, data);
    };

    const createHospitalIsNoGapProgram = async (data) => {
        const url = `HospitalsNoGapProgram`;

        return await doFetch(url, data);
    };

    const addHospitalNoGapProgramSurgeon = async (data) => {
        const url = `HospitalsNoGapProgram/Provider`;

        return await doFetch(url, data);
    };

    const addHospitalNoGapProgramRule = async (data) => {
        const url = `HospitalsNoGapProgram/SpecialRule`;

        return await doFetch(url, data);
    };

    const addAccountProviderNoGapProgram = async (data) => {
        const url = `AccountProviderNoGapProgram`;

        return await doFetch(url, data);
    };

    const sendCustomNotification = async (data) => {
        const url = `CustomNotifications`;

        return await doFetch(url, data);
    };

    const resetOrderProductAgreementDate = async (doctorId) => {
        const url = `AgreementDocuments/ResetOrderProductAgreement/${doctorId}`;

        return await doFetch(url, {});
    }

    export const postMethod = {
        logIn,
        signUp,
        sendVerificationEmail,
        validateEmail,
        sendVerificationSms,
        validatePhone,
        getMfaQrCode,
        sendAccountSignature,
        addBillingRule,
        forgetPwd,
        createAdmin,
        createAccount,
        createAccountAvatar,
        createAccountAvatarForPatient,
        createGroup,
        createExternalGroupInvoice,
        createAccountInvoice,
        sendInvoiceToDoctor,
        createPatient,
        createPatientContact,
        createSurgeon,
        createPatientEncounter,
        sendNotifyPatient,
        postPaymentEFTCSV,
        checkOPV,
        matchPatient,
        matchPatientEncounters,
        checkEligibility,
        claimPatientEncounter,
        claimEncounterStatus,
        claimEncounterEraStatus,
        addInsuranceFundToAccount,
        createPayment,
        resentEncounterNotification,
        cancelEncounterCommunication,
        sendEncounterCommunicationNow,
        sendAccountOnboardingForm,
        uploadAccountSchedulePDF,
        validateMfa,
        regeneratePdfFile,
        uploadPatientDocument,
        createEstimateFromBillingItem,
        sendEstimateToPatient,
        createInvoiceFromBillingItem,
        sendInvoiceToPatient,
        sendTopUpInvoice,
        sendInvoiceToSupport,
        sendReceiptToPatient,
        createAnnouncement,
        createWorkcoverAuthority,
        createTrafficAccidentAuthority,
        createManager,
        createAccountLeave,
        createAccountCalendarEvent,
        createGlobalEvent,
        createNotification,
        createAccountNotification,
        calculateBillingRuleTotalFee,
        updateBillingRuleTotalFee,
        createInvoiceForHealthFund,
        sendInvoiceToClaim,
        createAccountProvider,
        createSupportStaff,
        createEmailTemplate,
        createSmsTemplate,
        createPdfTemplate,
        cancelNotifyPatient,
        createNote,
        createPreOpInstruction,
        createPreOpQuestionnaire,
        sendPreOpQuestionnaire,
        createAccountPresets,
        createOcrSticker,
        createThirdPartyInsurer,
        createCalendarEventCategory,
        createDefaultFilter,
        uploadFile,
        createSurgeryRoom,
        createFreeTextItem,
        paymentRefundCollectPatientBankDetails,
        createAccountInsuranceDefaultCover,
        createBupaPracticeId,
        createAccountThirdPartyInsurer,
        createHospitalIsNoGapProgram,
        addHospitalNoGapProgramSurgeon,
        addHospitalNoGapProgramRule,
        addAccountProviderNoGapProgram,
        sendCustomNotification,
        createOrderProduct,
        createAgreementDocument,
        resetOrderProductAgreementDate,
        getInvoiceReason
    };
</script>
